import React from "react";
import { Col } from "reactstrap";
import { GetLanguageString, BouncingLoader } from "../helper/Components";

export const parseBalanceNumber = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  const cleaned = String(val).replace(/,/g, "").trim();
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const formatRs = (value) =>
  `Rs ${new Intl.NumberFormat().format(parseBalanceNumber(value))}`;

export const CashAccountBalanceCard = ({
  balance,
  effectiveBalance,
  ledgerBalance,
  pendingHolds,
  loading = false,
  colProps = { xs: "12", md: "6", className: "mb-3" },
  labelKey = "cash_account_balance",
  badgeText = "Effective Balance",
}) => {
  const effNum =
    effectiveBalance !== undefined && effectiveBalance !== null
      ? parseBalanceNumber(effectiveBalance)
      : parseBalanceNumber(balance);
  const ledgerNum =
    ledgerBalance !== undefined && ledgerBalance !== null
      ? parseBalanceNumber(ledgerBalance)
      : effNum;
  const holdsNum =
    pendingHolds !== undefined && pendingHolds !== null
      ? parseBalanceNumber(pendingHolds)
      : 0;

  return (
    <Col {...colProps}>
      <div
        style={{
          background: "linear-gradient(135deg, #3B5B72 0%, #2A4456 50%, #1E3342 100%)",
          color: "#ffffff",
          padding: "16px 20px",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.16)",
          boxShadow: "0 10px 25px -4px rgba(18, 38, 58, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "96px",
          transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          cursor: "default",
          gap: "16px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 16px 32px -4px rgba(18, 38, 58, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.28)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 10px 25px -4px rgba(18, 38, 58, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.22)";
        }}
      >
        {/* Left: Account Title & Breakdown */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.12)",
                backdropFilter: "blur(6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
              </svg>
            </div>
            <span style={{ fontSize: "14.5px", fontWeight: "700", letterSpacing: "0.2px", color: "#ffffff" }}>
              <GetLanguageString props={labelKey} />
            </span>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(0, 0, 0, 0.22)",
              border: "1px solid rgba(255, 255, 255, 0.16)",
              borderRadius: "20px",
              padding: "4px 14px",
              fontSize: "12.5px",
              color: "#ffffff",
              letterSpacing: "0.15px",
              width: "fit-content",
            }}
          >
            <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>
              Current Balance: <strong style={{ fontWeight: "800", color: "#ffffff" }}>{formatRs(ledgerNum)}</strong>
            </span>
            <span style={{ opacity: 0.35 }}>|</span>
            <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>
              Pending: <strong style={{ fontWeight: "800", color: "#ffffff" }}>{formatRs(holdsNum)}</strong>
            </span>
          </div>
        </div>

        {/* Right: Effective Balance Pill & Amount directly below */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
          <span
            style={{
              background: "rgba(255, 255, 255, 0.16)",
              border: "1px solid rgba(255, 255, 255, 0.24)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: "#ffffff",
              borderRadius: "20px",
              fontSize: "10.5px",
              fontWeight: "700",
              padding: "2px 10px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {badgeText}
          </span>
          <div
            style={{
              fontSize: "25px",
              fontWeight: "800",
              letterSpacing: "-0.5px",
              color: "#ffffff",
              textAlign: "right",
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            }}
          >
            {loading ? <BouncingLoader /> : formatRs(effNum)}
          </div>
        </div>
      </div>
    </Col>
  );
};

export const AmanatAccountBalanceCard = ({
  balance,
  effectiveBalance,
  ledgerBalance,
  pendingHolds,
  loading = false,
  colProps = { xs: "12", md: "6", className: "mb-3" },
  labelKey = "transactions_amanat_account_balance",
  badgeText = "Effective Balance",
}) => {
  const effNum =
    effectiveBalance !== undefined && effectiveBalance !== null
      ? parseBalanceNumber(effectiveBalance)
      : parseBalanceNumber(balance);
  const ledgerNum =
    ledgerBalance !== undefined && ledgerBalance !== null
      ? parseBalanceNumber(ledgerBalance)
      : effNum;
  const holdsNum =
    pendingHolds !== undefined && pendingHolds !== null
      ? parseBalanceNumber(pendingHolds)
      : 0;

  return (
    <Col {...colProps}>
      <div
        style={{
          background: "linear-gradient(135deg, #2D8E7E 0%, #1E6C5F 50%, #144E44 100%)",
          color: "#ffffff",
          padding: "16px 20px",
          borderRadius: "16px",
          border: "1px solid rgba(255, 255, 255, 0.16)",
          boxShadow: "0 10px 25px -4px rgba(18, 55, 48, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "96px",
          transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          cursor: "default",
          gap: "16px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 16px 32px -4px rgba(18, 55, 48, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.28)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 10px 25px -4px rgba(18, 55, 48, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.22)";
        }}
      >
        {/* Left: Account Title & Breakdown */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.12)",
                backdropFilter: "blur(6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
              </svg>
            </div>
            <span style={{ fontSize: "14.5px", fontWeight: "700", letterSpacing: "0.2px", color: "#ffffff" }}>
              <GetLanguageString props={labelKey} />
            </span>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(0, 0, 0, 0.22)",
              border: "1px solid rgba(255, 255, 255, 0.16)",
              borderRadius: "20px",
              padding: "4px 14px",
              fontSize: "12.5px",
              color: "#ffffff",
              letterSpacing: "0.15px",
              width: "fit-content",
            }}
          >
            <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>
              Current Balance: <strong style={{ fontWeight: "800", color: "#ffffff" }}>{formatRs(ledgerNum)}</strong>
            </span>
            <span style={{ opacity: 0.35 }}>|</span>
            <span style={{ color: "rgba(255, 255, 255, 0.85)" }}>
              Pending: <strong style={{ fontWeight: "800", color: "#ffffff" }}>{formatRs(holdsNum)}</strong>
            </span>
          </div>
        </div>

        {/* Right: Effective Balance Pill & Amount directly below */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
          <span
            style={{
              background: "rgba(255, 255, 255, 0.16)",
              border: "1px solid rgba(255, 255, 255, 0.24)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: "#ffffff",
              borderRadius: "20px",
              fontSize: "10.5px",
              fontWeight: "700",
              padding: "2px 10px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {badgeText}
          </span>
          <div
            style={{
              fontSize: "25px",
              fontWeight: "800",
              letterSpacing: "-0.5px",
              color: "#ffffff",
              textAlign: "right",
              textShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            }}
          >
            {loading ? <BouncingLoader /> : formatRs(effNum)}
          </div>
        </div>
      </div>
    </Col>
  );
};

export const MetricCard = ({
  labelKey,
  label,
  value,
  badgeText,
  icon,
  tone = "slate", // "slate" | "emerald"
  format = "currency", // "currency" | "integer" | "days"
  loading = false,
  colProps = { xs: "12", md: "6", className: "mb-3" },
}) => {
  const isEmerald = tone === "emerald";
  const bg = isEmerald
    ? "linear-gradient(135deg, #EBF7F4 0%, #DCF1EB 100%)"
    : "linear-gradient(135deg, #EEF4F8 0%, #E2EDF4 100%)";
  const border = isEmerald
    ? "1px solid rgba(53, 160, 142, 0.25)"
    : "1px solid rgba(72, 106, 132, 0.22)";
  const shadow = isEmerald
    ? "0 6px 20px rgba(35, 122, 110, 0.08)"
    : "0 6px 20px rgba(72, 106, 132, 0.08)";
  const hoverShadow = isEmerald
    ? "0 10px 24px rgba(35, 122, 110, 0.14)"
    : "0 10px 24px rgba(72, 106, 132, 0.14)";
  const textColor = isEmerald ? "#1B6258" : "#2D4455";
  const valueColor = isEmerald ? "#144D44" : "#1F384C";
  const badgeBg = isEmerald ? "rgba(53, 160, 142, 0.14)" : "rgba(72, 106, 132, 0.12)";
  const badgeBorder = isEmerald ? "1px solid rgba(53, 160, 142, 0.22)" : "1px solid rgba(72, 106, 132, 0.18)";

  return (
    <Col {...colProps}>
      <div
        style={{
          background: bg,
          border,
          color: textColor,
          padding: "14px 18px",
          borderRadius: "14px",
          boxShadow: shadow,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: "95px",
          transition: "all 0.25s ease-in-out",
          cursor: "default",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = hoverShadow;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = shadow;
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {icon}
            <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.2px", color: textColor }}>
              {labelKey ? <GetLanguageString props={labelKey} /> : label}
            </span>
          </div>
          {badgeText && (
            <span
              style={{
                background: badgeBg,
                border: badgeBorder,
                color: textColor,
                borderRadius: "999px",
                fontSize: "10px",
                fontWeight: "600",
                padding: "2px 10px",
              }}
            >
              {badgeText}
            </span>
          )}
        </div>
        <div style={{ fontSize: "22px", fontWeight: "800", marginTop: "6px", letterSpacing: "-0.4px", color: valueColor }}>
          {loading ? (
            <BouncingLoader />
          ) : format === "integer" ? (
            new Intl.NumberFormat().format(Number(value) || 0)
          ) : format === "days" ? (
            `${new Intl.NumberFormat().format(Number(value) || 0)}`
          ) : (
            formatRs(value)
          )}
        </div>
      </div>
    </Col>
  );
};
